import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useAudioSync - Web Audio API based playback with mixing support
 */
export function useAudioSync({ songUrl, onTimeUpdate }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [volume, setVolumeState] = useState(1.0);
  const [error, setError] = useState(null);

  const audioCtxRef = useRef(null);
  const audioBufferRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  const startTimeRef = useRef(0);
  const offsetRef = useRef(0);
  const animFrameRef = useRef(null);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    if (!songUrl) return;
    setIsLoading(true);
    setError(null);

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;

    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    gain.connect(ctx.destination);
    gainNodeRef.current = gain;

    fetch(songUrl)
      .then(r => r.arrayBuffer())
      .then(buf => ctx.decodeAudioData(buf))
      .then(decoded => {
        audioBufferRef.current = decoded;
        setDuration(decoded.duration);
        setIsLoading(false);
      })
      .catch(err => {
        setError('Failed to load audio');
        setIsLoading(false);
      });

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      try { sourceNodeRef.current?.stop(); } catch(e) {}
      ctx.close();
    };
  }, [songUrl]);

  const getPosition = useCallback(() => {
    if (!audioCtxRef.current) return 0;
    if (!isPlayingRef.current) return offsetRef.current;
    return offsetRef.current + (audioCtxRef.current.currentTime - startTimeRef.current);
  }, []);

  const stopTracking = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
  }, []);

  const startTracking = useCallback(() => {
    const tick = () => {
      const pos = getPosition();
      setCurrentTime(pos);
      onTimeUpdate?.(pos, true);
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  }, [getPosition, onTimeUpdate]);

  const play = useCallback((fromOffset) => {
    const ctx = audioCtxRef.current;
    const buf = audioBufferRef.current;
    if (!ctx || !buf) return;
    if (ctx.state === 'suspended') ctx.resume();
    try { sourceNodeRef.current?.stop(); } catch(e) {}

    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.connect(gainNodeRef.current);

    const startOffset = fromOffset !== undefined ? fromOffset : offsetRef.current;
    source.start(0, Math.max(0, startOffset));
    sourceNodeRef.current = source;
    startTimeRef.current = ctx.currentTime;
    offsetRef.current = startOffset;

    source.onended = () => {
      if (sourceNodeRef.current === source) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        stopTracking();
      }
    };

    isPlayingRef.current = true;
    setIsPlaying(true);
    startTracking();
  }, [startTracking, stopTracking]);

  const pause = useCallback(() => {
    offsetRef.current = getPosition();
    try { sourceNodeRef.current?.stop(); } catch(e) {}
    sourceNodeRef.current = null;
    isPlayingRef.current = false;
    setIsPlaying(false);
    stopTracking();
  }, [getPosition, stopTracking]);

  const stop = useCallback(() => {
    offsetRef.current = 0;
    try { sourceNodeRef.current?.stop(); } catch(e) {}
    sourceNodeRef.current = null;
    isPlayingRef.current = false;
    setIsPlaying(false);
    setCurrentTime(0);
    stopTracking();
  }, [stopTracking]);

  const seek = useCallback((time) => {
    const wasPlaying = isPlayingRef.current;
    try { sourceNodeRef.current?.stop(); } catch(e) {}
    sourceNodeRef.current = null;
    stopTracking();
    offsetRef.current = time;
    setCurrentTime(time);
    if (wasPlaying) play(time);
  }, [play, stopTracking]);

  const setVolume = useCallback((val) => {
    setVolumeState(val);
    if (gainNodeRef.current) gainNodeRef.current.gain.value = val;
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) pause();
    else play();
  }, [play, pause]);

  const getAudioContext = useCallback(() => audioCtxRef.current, []);

  return {
    isPlaying, currentTime, duration, isLoading, error,
    volume, play, pause, stop, seek, setVolume, togglePlay, getAudioContext,
  };
}

/**
 * useRecorder - Microphone recording only (mic track uploaded separately)
 */
export function useRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [micPermission, setMicPermission] = useState('unknown');
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicPermission('granted');
      return true;
    } catch (err) {
      setMicPermission('denied');
      setError('Microphone permission denied');
      return false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      let stream = streamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      }

      chunksRef.current = [];
      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setRecordedBlob(blob);
      };

      mediaRecorder.start(100);
      mediaRecorderRef.current = mediaRecorder;
      startTimeRef.current = Date.now();
      setIsRecording(true);
      setRecordedBlob(null);

      timerRef.current = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (err) {
      setError('Failed to start recording: ' + err.message);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setRecordingTime(0);
  }, []);

  const discardRecording = useCallback(() => {
    setRecordedBlob(null);
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return {
    isRecording, recordedBlob, recordingTime,
    micPermission, error,
    requestPermission, startRecording, stopRecording, discardRecording,
  };
}

/**
 * useMixer - Browser-side audio mixing using OfflineAudioContext
 * Mixes backing track + vocal into a single WAV file ready for upload
 */
export function useMixer() {
  const [isMixing, setIsMixing] = useState(false);
  const [mixProgress, setMixProgress] = useState(0);
  const [mixError, setMixError] = useState(null);

  const mix = useCallback(async ({
    backingTrackUrl,
    vocalBlob,
    backingVolume = 1.0,
    vocalVolume = 1.0,
    syncOffset = 0,
  }) => {
    setIsMixing(true);
    setMixError(null);
    setMixProgress(10);

    try {
      // Load both audio files as ArrayBuffers
      const [backingArray, vocalArray] = await Promise.all([
        fetch(backingTrackUrl).then(r => r.arrayBuffer()),
        vocalBlob.arrayBuffer(),
      ]);
      setMixProgress(40);

      // Decode using a temporary context
      const tempCtx = new AudioContext();
      const [backingBuffer, vocalBuffer] = await Promise.all([
        tempCtx.decodeAudioData(backingArray),
        tempCtx.decodeAudioData(vocalArray),
      ]);
      tempCtx.close();
      setMixProgress(60);

      // Calculate timing
      const vocalStartTime = Math.max(0, syncOffset);
      const backingStartOffset = Math.max(0, -syncOffset);
      const totalDuration = Math.max(
        backingBuffer.duration - backingStartOffset,
        vocalBuffer.duration + vocalStartTime
      );

      const sampleRate = 44100;
      const offlineCtx = new OfflineAudioContext({
        numberOfChannels: 2,
        length: Math.ceil(totalDuration * sampleRate),
        sampleRate,
      });

      // Backing track node
      const backingSource = offlineCtx.createBufferSource();
      backingSource.buffer = backingBuffer;
      const backingGain = offlineCtx.createGain();
      backingGain.gain.value = Math.max(0, Math.min(2, backingVolume));
      backingSource.connect(backingGain);
      backingGain.connect(offlineCtx.destination);
      backingSource.start(0, Math.max(0, backingStartOffset));

      // Vocal track node
      const vocalSource = offlineCtx.createBufferSource();
      vocalSource.buffer = vocalBuffer;
      const vocalGain = offlineCtx.createGain();
      vocalGain.gain.value = Math.max(0, Math.min(2, vocalVolume));
      vocalSource.connect(vocalGain);
      vocalGain.connect(offlineCtx.destination);
      vocalSource.start(Math.max(0, vocalStartTime));

      setMixProgress(70);
      const renderedBuffer = await offlineCtx.startRendering();
      setMixProgress(90);

      const wavBlob = audioBufferToWav(renderedBuffer);
      setMixProgress(100);
      return wavBlob;

    } catch (err) {
      setMixError('Mixing failed: ' + err.message);
      return null;
    } finally {
      setIsMixing(false);
    }
  }, []);

  return { mix, isMixing, mixProgress, mixError };
}

// ---- WAV encoder ----
function audioBufferToWav(buffer) {
  const numChannels = Math.min(buffer.numberOfChannels, 2);
  const sampleRate = buffer.sampleRate;
  const bitDepth = 16;
  const dataLength = buffer.length * numChannels * (bitDepth / 8);
  const wavBuffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(wavBuffer);

  const ws = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  ws(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  ws(8, 'WAVE');
  ws(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
  view.setUint16(32, numChannels * (bitDepth / 8), true);
  view.setUint16(34, bitDepth, true);
  ws(36, 'data');
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([wavBuffer], { type: 'audio/wav' });
}

function getSupportedMimeType() {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'];
  return types.find(t => MediaRecorder.isTypeSupported(t)) || null;
}
